<script setup>
// Agenda Builder promo banner (Figma 4583:183658). A dumb presentational bar
// shown above the agenda tabs; agenda.vue owns visibility gating and the build
// handler. The "Build My Schedule" CTA emits `build` (opens the Agenda Builder);
// "See how it works" is a plain link to the explainer blog (UNB-607).
defineEmits(['build']);
</script>

<template>
    <div class="sb-banner" data-test="schedule-builder-banner">
        <p class="sb-banner__copy">
            Let our Agenda Builder personalize your schedule in minutes.
            <NuxtLink to="/blog/how-to-build-your-unbound-schedule-in-minutes" class="sb-banner__link">See how it works</NuxtLink>
        </p>

        <button type="button" class="sb-banner__cta" @click="$emit('build')">
            <svg viewBox="0 0 13 13" fill="none" class="size-[13px] shrink-0" aria-hidden="true">
                <path d="M13 8.09719C13 8.21502 12.9044 8.31057 12.7875 8.31057C10.9214 8.31057 8.71296 10.5186 8.71296 12.3843C8.71296 12.5021 8.61738 12.5977 8.50046 12.5977C8.38354 12.5977 8.28797 12.5021 8.28797 12.3843C8.28704 10.5186 6.07856 8.31057 4.2125 8.31057C4.09465 8.31057 4 8.21502 4 8.09719C4 7.97937 4.09558 7.88381 4.2125 7.88381C6.07856 7.88381 8.28704 5.67579 8.28704 3.81104C8.28704 3.69321 8.38262 3.59766 8.49954 3.59766C8.61645 3.59766 8.71203 3.69321 8.71203 3.81104C8.71203 5.67579 10.9205 7.88381 12.7866 7.88381C12.9044 7.88381 12.9991 7.97937 12.9991 8.09719H13Z" fill="currentColor" />
                <path d="M6 3.59735C6 3.6759 5.93628 3.7396 5.85834 3.7396C4.61429 3.7396 3.14197 5.21161 3.14197 6.4554C3.14197 6.53395 3.07826 6.59766 3.00031 6.59766C2.92236 6.59766 2.85865 6.53395 2.85865 6.4554C2.85803 5.21161 1.38571 3.7396 0.141664 3.7396C0.0630993 3.7396 0 3.6759 0 3.59735C0 3.5188 0.0637179 3.45509 0.141664 3.45509C1.38571 3.45509 2.85803 1.98308 2.85803 0.73991C2.85803 0.661361 2.92174 0.597656 2.99969 0.597656C3.07764 0.597656 3.14135 0.661361 3.14135 0.73991C3.14135 1.98308 4.61367 3.45509 5.85772 3.45509C5.93628 3.45509 5.99938 3.5188 5.99938 3.59735H6Z" fill="currentColor" />
            </svg>
            Build My Schedule
        </button>
    </div>
</template>

<style scoped>
.sb-banner {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    gap: 26px;
    background: var(--magenta);
    border-radius: var(--radius);
    padding: 24px 24px 30px;
}

.sb-banner__copy {
    margin: 0;
    color: #fff;
    font-family: var(--font-hubspot-serif);
    font-size: 22px;
    line-height: 1.3;
}

.sb-banner__link {
    color: #fff;
    text-decoration: underline dotted;
    text-decoration-color: var(--pink);
    text-underline-offset: 3px;
    text-decoration-skip-ink: none;
    text-underline-position: from-font;
    cursor: pointer;
}

.sb-banner__link:hover {
    text-decoration: none;
}

.sb-banner__cta {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
    flex-shrink: 0;
    width: fit-content;
    border-radius: 50px;
    border: 1px solid #fff;
    background: transparent;
    color: #fff;
    padding: 10px 20px;
    font-family: var(--font-hubspot-serif);
    font-size: calc(16 / var(--base-fs) * 1rem);
    line-height: 1.3;
    letter-spacing: 0.32px;
    cursor: pointer;
    transition: background-color 0.2s ease, color 0.2s ease;
}

.sb-banner__cta:hover {
    background: #fff;
    color: var(--magenta);
}

@media (min-width: 1024px) {
    .sb-banner {
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        /* At least 80px between the copy and the Build My Schedule button (UNB-611):
           space-between pushes them apart, but with a long copy line the gap can
           collapse to this floor, so the floor is the guarantee. */
        gap: 80px;
        padding: 33px 24px 33px 38px;
    }

    .sb-banner__copy {
        font-size: 24px;
    }

    .sb-banner__cta {
        gap: 8px;
        font-size: calc(17 / var(--base-fs) * 1rem);
        line-height: 1.4;
        letter-spacing: 0.34px;
    }
}
</style>
